import { pxToRem } from "@mos-cat/ds-theme";
import type { ElementType, ReactNode, Ref } from "react";
import { Box } from "../Box/Box";
import type { CheckboxProps } from "../Checkbox/Checkbox";
import { Checkbox } from "../Checkbox/Checkbox";
import type { FieldProps } from "../Field/Field";
import { Field } from "../Field/Field";
import type { RadioProps } from "../Radio/Radio";
import { Radio } from "../Radio/Radio";
import type { StackProps } from "../Stack/Stack";
import { Stack } from "../Stack/Stack";
import type { PropsWithAs } from "../as";
import { forwardRefWithAs } from "../as";
import type { StylesOptions } from "../hooks";
import { LabelText } from "../LabelText/LabelText";
import { ErrorText } from "../ErrorText/ErrorText";
import { HintText } from "../HintText/HintText";

type ModeType = "fieldset" | "field";

/**
 * @beta
 */
export type LabelledControlProps = {
  error?: string | string[];
  hint?: ReactNode;
  label?: ReactNode;
  mode?: ModeType;
  disabled?: boolean;
} & StylesOptions &
  Partial<Pick<StackProps, "maxWidth" | "width" | "className">>;

function getOptions(mode: ModeType) {
  if (mode === "fieldset") {
    return { wrapperType: "fieldset", contentType: "legend" };
  }
  return { wrapperType: "label", contentType: "span" };
}

function LabelledWrapper({
  mode = "field",
  error,
  hint,
  label,
  className,
  children,
  styles,
  disabled,
  ...props
}: PropsWithAs<LabelledControlProps & { children: ReactNode }, ElementType>) {
  const { wrapperType, contentType } = getOptions(mode);
  const errors = Array.isArray(error) ? error : [error].filter(Boolean);
  return (
    <Stack
      space={mode === "field" ? "labelledFieldMarginTop" : "sp-16"}
      as={wrapperType as ElementType}
      display="inline-flex"
      maxWidth={props.maxWidth}
      width={props.width}
      className={className}
      styles={{
        ...styles,
        label: "LabelledWrapper",
        pointerEvents: disabled ? "none" : "auto",
      }}
    >
      <Stack as={contentType as ElementType} space="sp-4" styles={{ label: "TitleGroup" }}>
        {label && <LabelText>{label}</LabelText>}
        {hint && (
          <Box display="block" as={HintText}>
            {hint}
          </Box>
        )}
        <>
          {errors.map((message, index) => (
            // eslint-disable-next-line react/no-array-index-key -- некритично
            <Box key={index} as={ErrorText} display="block">
              {message}
            </Box>
          ))}
        </>
      </Stack>
      <Box>{children}</Box>
    </Stack>
  );
}

/**
 * @beta
 */
export const LabelledControl = forwardRefWithAs<LabelledControlProps, ElementType>(
  function LabelledControl(
    {
      as: Component = "input",
      mode = "field",
      error,
      hint,
      label,
      className,
      styles,
      disabled,
      ...props
    }: PropsWithAs<LabelledControlProps, ElementType>,
    ref: Ref<Element>
  ): JSX.Element {
    return (
      <LabelledWrapper
        error={error}
        mode={mode}
        hint={hint}
        label={label}
        className={className}
        width={props.width ?? "100%"}
        maxWidth={props.maxWidth ?? "100%"}
        styles={styles}
        disabled={disabled}
      >
        <Component {...props} ref={ref} />
      </LabelledWrapper>
    );
  }
);

/**
 * @beta
 */
export type LabelledFieldProps = Omit<LabelledControlProps, "mode"> & FieldProps;

/**
 * @beta
 */
export const LabelledField = forwardRefWithAs<LabelledFieldProps, ElementType>(
  function LabelledField(
    {
      as = "input",
      error,
      hint,
      label,
      disabled,
      ...props
    }: PropsWithAs<LabelledFieldProps, ElementType>,
    ref: Ref<HTMLInputElement>
  ): JSX.Element {
    return (
      <LabelledWrapper
        error={error}
        mode="field"
        hint={hint}
        label={label}
        width={props.width ?? "100%"}
        maxWidth={props.maxWidth ?? pxToRem(724)}
        className={props.className}
        disabled={disabled}
      >
        <Field as={as} disabled={disabled} {...props} ref={ref} />
      </LabelledWrapper>
    );
  }
);

type LabelledRadioAndCheckboxProps = {
  label?: ReactNode;
  maxWidth?: string | number;
};

/**
 * @deprecated
 */
export type LabelledRadioProps = LabelledRadioAndCheckboxProps & RadioProps;

/**
 * @deprecated Используйте компонент Radio
 */
export const LabelledRadio = forwardRefWithAs<LabelledRadioProps, ElementType<HTMLInputElement>>(
  function LabelledRadio(
    {
      label,
      className,
      disabled,
      styles,
      width,
      maxWidth,
      ...props
    }: PropsWithAs<LabelledRadioProps, ElementType<HTMLInputElement>>,
    ref: Ref<HTMLInputElement>
  ): JSX.Element {
    return (
      <Box
        display="flex"
        className={className}
        width={width ?? 1}
        maxWidth={maxWidth ?? 1}
        styles={{
          ...styles,
          lineHeight: "checkableLabelLineHeight",
        }}
      >
        <Radio disabled={disabled} label={label} width={1} {...props} ref={ref} />
      </Box>
    );
  }
);

/**
 * @deprecated
 */
export type LabelledCheckboxProps = LabelledRadioAndCheckboxProps & CheckboxProps;

/**
 * @deprecated Используйте компонент Checkbox
 */
export const LabelledCheckbox = forwardRefWithAs<
  LabelledCheckboxProps,
  ElementType<HTMLInputElement>
>(function LabelledCheckbox(
  {
    label,
    className,
    disabled,
    styles,
    width,
    maxWidth,
    ...props
  }: PropsWithAs<LabelledCheckboxProps, ElementType<HTMLInputElement>>,
  ref: Ref<HTMLInputElement>
): JSX.Element {
  return (
    <Box
      display="flex"
      className={className}
      width={width ?? 1}
      maxWidth={maxWidth ?? 1}
      styles={{
        ...styles,
        lineHeight: "checkableLabelLineHeight",
      }}
    >
      <Checkbox disabled={disabled} label={label} width={1} {...props} ref={ref} />
    </Box>
  );
});

type LabelledFieldsetProps = Omit<LabelledControlProps, "mode">;

export const LabelledFieldset = forwardRefWithAs<
  LabelledFieldsetProps,
  ElementType<HTMLFieldSetElement>
>(function LabelledFieldset(
  props: PropsWithAs<LabelledFieldsetProps, ElementType<HTMLFieldSetElement>>,
  ref: Ref<Element>
): JSX.Element {
  return <LabelledControl {...props} mode="fieldset" ref={ref} />;
});
